import { Link } from "react-router-dom";
import { ThemeToggle } from "./ui/ThemeToggle.js";

const outcomes = [
  { number: "01", title: "Contract once", text: "One guided application opens the door to the carriers that fit your business." },
  { number: "02", title: "See every move", text: "Clear status updates replace inbox archaeology and unanswered follow-ups." },
  { number: "03", title: "Grow your agency", text: "Bring on producers, manage downlines, and keep momentum in one workspace." },
];

const resources = [
  ["Contracting", "Your field guide to a faster carrier appointment"],
  ["Compliance", "Five habits that protect a growing book"],
  ["Agency growth", "Build a downline people want to stay in"],
];

function Arrow() {
  return <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14m-5-5 5 5-5 5" /></svg>;
}

export function MarketingHomePage() {
  return (
    <div className="min-h-screen overflow-hidden bg-surface-page text-content-primary">
      <header className="relative z-20 border-b border-border-subtle bg-surface-page/90 backdrop-blur">
        <div className="mx-auto flex h-20 max-w-[108rem] items-center justify-between px-5 sm:px-8 lg:px-12">
          <Link to="/" className="focus-ring flex items-center gap-3 rounded-lg">
            <span className="altura-logo-mark flex h-10 w-10 items-center justify-center rounded-[10px] font-display text-lg font-extrabold text-white">A</span>
            <span className="font-display text-lg font-extrabold tracking-tight">Altura <span className="text-content-muted">of America</span></span>
          </Link>
          <nav className="hidden items-center gap-8 text-sm font-semibold md:flex" aria-label="Main navigation">
            <a href="#platform" className="hover:text-accent">Platform</a>
            <a href="#approach" className="hover:text-accent">Our approach</a>
            <a href="#resources" className="hover:text-accent">Resources</a>
          </nav>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Link to="/login" className="focus-ring rounded-[10px] border border-border-default bg-surface-raised px-4 py-2.5 text-sm font-bold transition-colors hover:bg-surface-overlay">Sign in</Link>
          </div>
        </div>
      </header>

      <main>
        <section className="public-grid relative">
          <div className="mx-auto grid min-h-[680px] max-w-[108rem] lg:grid-cols-[1.12fr_.88fr]">
            <div className="flex flex-col justify-center px-5 py-20 sm:px-8 lg:px-12 lg:py-28">
              <div className="mb-8 inline-flex w-fit items-center gap-2 rounded-full border border-border-default bg-surface-raised px-3 py-1.5 text-xs font-bold uppercase tracking-[0.14em]">
                <span className="h-2 w-2 rounded-full bg-[#ef674b]" />
                Built for independent insurance
              </div>
              <h1 className="max-w-5xl font-display text-[clamp(3rem,7vw,7.5rem)] font-extrabold leading-[0.91] tracking-[-0.065em]">
                Your business.<br /><span className="text-[#6c4ed8] dark:text-[#bca7ff]">Finally moving</span><br />at your speed.
              </h1>
              <p className="mt-8 max-w-2xl text-lg leading-relaxed text-content-secondary sm:text-xl">
                Contract, track, and grow without the paperwork drag. Altura gives independent agents one clear place to build what comes next.
              </p>
              <div className="mt-10 flex flex-col gap-3 sm:flex-row">
                <Link to="/login" className="focus-ring inline-flex min-h-12 items-center justify-center gap-3 rounded-[10px] bg-content-primary px-6 py-3 font-bold text-surface-raised transition-transform hover:-translate-y-0.5 motion-reduce:transform-none">
                  Open your portal <Arrow />
                </Link>
                <a href="#platform" className="focus-ring inline-flex min-h-12 items-center justify-center rounded-[10px] border border-border-default bg-surface-raised px-6 py-3 font-bold hover:bg-surface-overlay">See how it works</a>
              </div>
            </div>
            <div className="relative min-h-[440px] overflow-hidden border-t border-border-subtle bg-[#c8ef63] lg:min-h-full lg:border-l lg:border-t-0">
              <div className="absolute -right-16 top-12 h-64 w-64 rounded-full bg-[#ff765c]" />
              <div className="absolute -left-12 bottom-24 h-52 w-52 rotate-12 rounded-[12px] bg-[#ffd45e]" />
              <div className="absolute inset-8 flex items-center justify-center sm:inset-16">
                <div className="relative w-full max-w-xl rotate-[-2deg] rounded-[12px] border-2 border-[#292a25] bg-[#fcfbf7] p-5 shadow-[10px_10px_0_#292a25] sm:p-7">
                  <div className="flex items-center justify-between border-b border-[#d8d4c9] pb-4">
                    <div><p className="text-xs font-bold uppercase tracking-widest text-[#77786f]">Contracting workspace</p><p className="mt-1 font-display text-xl font-extrabold text-[#22231f]">Good morning, Maya</p></div>
                    <span className="rounded-full bg-[#eee9ff] px-3 py-1 text-xs font-bold text-[#6544d2]">6 active</span>
                  </div>
                  <div className="mt-5 grid gap-3 sm:grid-cols-3">
                    {[["12", "Carriers"], ["04", "In review"], ["08", "Appointed"]].map(([value, label], index) => (
                      <div key={label} className={`rounded-[9px] border border-[#d8d4c9] p-4 ${index === 1 ? "bg-[#fff0e9]" : "bg-white"}`}>
                        <p className="font-display text-3xl font-extrabold text-[#22231f]">{value}</p><p className="mt-1 text-xs font-bold text-[#77786f]">{label}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 space-y-2">
                    {["Transamerica", "Aflac", "Foresters"].map((carrier, index) => (
                      <div key={carrier} className="flex items-center justify-between rounded-[8px] bg-[#f0ede4] px-4 py-3 text-sm font-bold text-[#22231f]">
                        {carrier}<span className={index === 2 ? "text-[#ef674b]" : "text-[#258e76]"}>{index === 2 ? "Review" : "Active"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="platform" className="border-y border-border-subtle bg-surface-base py-20 sm:py-28">
          <div className="mx-auto max-w-[108rem] px-5 sm:px-8 lg:px-12">
            <div className="grid gap-8 lg:grid-cols-[.75fr_1.25fr]">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-accent">The platform</p>
                <h2 className="mt-4 max-w-xl font-display text-4xl font-extrabold leading-tight tracking-[-0.04em] sm:text-6xl">Less chasing.<br />More building.</h2>
              </div>
              <div className="grid border-t border-border-default sm:grid-cols-3 lg:border-l lg:border-t-0">
                {outcomes.map((item) => (
                  <article key={item.number} className="border-b border-border-default p-6 sm:border-b-0 sm:border-r lg:p-8">
                    <span className="font-display text-sm font-extrabold text-accent">{item.number}</span>
                    <h3 className="mt-10 font-display text-2xl font-extrabold">{item.title}</h3>
                    <p className="mt-3 leading-relaxed text-content-secondary">{item.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="approach" className="bg-[#6c4ed8] py-20 text-white sm:py-28">
          <div className="mx-auto grid max-w-[108rem] gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:px-12">
            <p className="font-display text-4xl font-extrabold leading-tight tracking-[-0.04em] sm:text-6xl">Big-agency infrastructure.<br /><span className="text-[#c8ef63]">Independent spirit.</span></p>
            <div className="max-w-2xl text-lg leading-relaxed text-white/80">
              <p>Altura combines practical technology with people who understand the contracting process. You get clarity when things are moving—and real support when they are not.</p>
              <Link to="/login" className="focus-ring mt-8 inline-flex items-center gap-3 rounded-[10px] bg-[#c8ef63] px-6 py-3 font-bold text-[#22231f]">Get started <Arrow /></Link>
            </div>
          </div>
        </section>

        <section id="resources" className="bg-surface-page py-20 sm:py-28">
          <div className="mx-auto max-w-[108rem] px-5 sm:px-8 lg:px-12">
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end"><div><p className="text-xs font-bold uppercase tracking-[0.16em] text-accent">Field notes</p><h2 className="mt-3 font-display text-4xl font-extrabold tracking-[-0.04em] sm:text-5xl">Useful by design.</h2></div><p className="max-w-md text-content-secondary">Straightforward guidance for producers and the people building agencies.</p></div>
            <div className="mt-12 grid gap-4 lg:grid-cols-3">
              {resources.map(([category, title], index) => (
                <article key={title} className={`rounded-[12px] border border-border-default p-7 ${index === 0 ? "bg-[#ffd45e] text-[#22231f]" : index === 1 ? "bg-surface-raised" : "bg-[#c8ef63] text-[#22231f]"}`}>
                  <p className="text-xs font-bold uppercase tracking-[0.14em] opacity-70">{category}</p><h3 className="mt-12 font-display text-2xl font-extrabold leading-tight">{title}</h3><span className="mt-6 inline-flex items-center gap-2 text-sm font-bold">Read the guide <Arrow /></span>
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-border-subtle bg-surface-base">
        <div className="mx-auto flex max-w-[108rem] flex-col gap-6 px-5 py-10 sm:flex-row sm:items-center sm:justify-between sm:px-8 lg:px-12">
          <p className="font-display font-extrabold">Altura of America</p>
          <p className="text-sm text-content-muted">© {new Date().getFullYear()} Altura. Built for independent momentum.</p>
          <Link to="/login" className="text-sm font-bold hover:text-accent">Agent sign in →</Link>
        </div>
      </footer>
    </div>
  );
}
